/**
 * Operations Executive Scorecard - Board Level Premium Design
 * McKinsey-Level Executive Scorecard for Operations
 * 5 Pillars × 3 KPIs = 15 Total KPIs
 * 
 * ESTRUTURA:
 * - 1 KPI Principal (destaque visual)
 * - 2 KPIs de Suporte (menor peso relativo)
 * 
 * VALIDAÇÃO DE KPIs:
 * Todos os KPIs são baseados em métricas disponíveis em:
 * - Relatórios mensais operacionais
 * - POA / Orçamento 2025
 * - Relatório de Gestão 2025
 */

import React from 'react';
import { 
  TrendingUp, TrendingDown, Pickaxe, Factory, Layers, Gauge, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Activity
} from 'lucide-react';
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useLanguage } from '../../context/LanguageContext';

// ============ PREMIUM DESIGN TOKENS ============
const colors = {
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#7A859D',
    inverse: '#FFFFFF',
  },
  bg: {
    page: '#F5F7FB',
    card: '#FFFFFF',
    dark: '#0D1538',
    header: '#0D1538',
    surface: '#FFFFFF',
    surfaceAlt: '#F8FAFC',
  },
  border: {
    default: '#E3E8F1',
    soft: '#EEF2F7',
  },
  status: {
    positive: '#0C8A4B',
    positiveBg: '#EAF7EF',
    positiveBorder: '#A7F3D0',
    warning: '#A07A18',
    warningBg: '#F8F1DD',
    warningBorder: '#FDE68A',
    negative: '#C33D52',
    negativeBg: '#FBECEF',
    negativeBorder: '#FECACA',
  },
  accent: {
    gold: '#B89A3C',
    goldBg: '#FBF8F0',
  },
  pillars: {
    mine: '#3B82F6',
    plant: '#8B5CF6',
    stocks: '#F59E0B',
    efficiency: '#10B981',
    risk: '#EF4444',
  }
};

const typography = {
  sectionTitle: 'font-serif text-[28px] leading-[36px] font-semibold tracking-[-0.2px]',
  pillarTitle: 'font-sans text-[13px] leading-[18px] font-bold tracking-[0.3px] uppercase',
  pillarQuestion: 'font-sans text-[11px] leading-[16px] font-normal italic',
  cardLabel: 'font-sans text-[12px] leading-[16px] font-semibold tracking-[0.4px]',
  kpiValue: 'font-sans text-[28px] leading-[32px] font-bold tracking-[-0.5px]',
  kpiUnit: 'font-sans text-[12px] leading-[16px] font-normal',
  smallText: 'font-sans text-[13px] leading-[16px] font-normal',
  microText: 'font-sans text-[11px] leading-[14px] font-medium tracking-[0.2px]',
};

// ============ SCORECARD DATA - VALIDATED KPIs (3 per pillar) ============
const scorecardData = {
  2025: {
    // 1. MINA E ACESSO AO MINÉRIO
    // Pergunta: Há minério suficiente e acessível?
    // KPIs: Minério Extraído (Principal), Estéril Removido, Strip Ratio
    // Removido: Perfuração de Produção (fica na página de detalhe)
    mine: {
      pillar: { pt: 'Mina e Acesso', en: 'Mine & Access' },
      question: { pt: 'Há minério suficiente e acessível?', en: 'Is there enough accessible ore?' },
      icon: Pickaxe,
      color: colors.pillars.mine,
      kpis: [
        { 
          id: 'ore_extracted', 
          label: { pt: 'Minério Extraído', en: 'Ore Extracted' }, 
          definition: { pt: 'Volume de minério extraído da mina', en: 'Ore volume extracted from mine' },
          actual: 4320000, budget: 4500000, yoy: 4150000, unit: 'm³',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Volume de minério extraído da mina e disponibilizado para processamento', en: 'Ore volume extracted from mine and made available for processing' },
          isPrimary: true
        },
        { 
          id: 'waste_removed', 
          label: { pt: 'Estéril Removido', en: 'Waste Removed' }, 
          definition: { pt: 'Volume de material estéril removido', en: 'Waste material volume removed' },
          actual: 8420000, budget: 9450000, yoy: 8520000, unit: 'm³',
          inverse: true,
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Volume de estéril removido para acesso ao minério - menor volume com mesmo acesso é favorável', en: 'Waste removed for ore access - lower volume with same access is favorable' }
        },
        { 
          id: 'strip_ratio', 
          label: { pt: 'Strip Ratio', en: 'Strip Ratio' }, 
          definition: { pt: 'Rácio estéril/minério', en: 'Waste/ore ratio' },
          actual: 1.95, budget: 2.10, yoy: 2.05, unit: ':1',
          inverse: true,
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Rácio estéril/minério - quanto menor, mais eficiente o acesso ao minério', en: 'Waste/ore ratio - lower is more efficient ore access' }
        },
      ]
    },
    // 2. PLANTA E RECUPERAÇÃO
    // Pergunta: A planta converte minério em quilates com eficiência?
    // KPIs: Minério Tratado (Principal), Teor de Alimentação, Tamanho Médio da Pedra
    // Removido: N.º de Pedras Especiais (fica na página de detalhe)
    plant: {
      pillar: { pt: 'Planta e Recuperação', en: 'Plant & Recovery' },
      question: { pt: 'A planta converte minério em quilates?', en: 'Is plant converting ore to carats?' },
      icon: Factory,
      color: colors.pillars.plant,
      kpis: [
        { 
          id: 'ore_processed', 
          label: { pt: 'Minério Tratado', en: 'Ore Processed' }, 
          definition: { pt: 'Volume de minério processado na planta', en: 'Ore volume processed in plant' },
          actual: 4280000, budget: 4400000, yoy: 4050000, unit: 'm³',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Volume total de minério processado na planta de tratamento', en: 'Total ore volume processed in treatment plant' },
          isPrimary: true
        },
        { 
          id: 'feed_grade', 
          label: { pt: 'Teor de Alimentação', en: 'Feed Grade' }, 
          definition: { pt: 'Quilates por 100 toneladas alimentadas', en: 'Carats per 100 tonnes fed' },
          actual: 16.02, budget: 15.80, yoy: 15.45, unit: 'cpht',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Teor médio de diamantes no minério alimentado à planta (cpht = quilates por 100 toneladas)', en: 'Average diamond grade in ore fed to plant (cpht = carats per 100 tonnes)' }
        },
        { 
          id: 'avg_stone_size', 
          label: { pt: 'Tamanho Médio da Pedra', en: 'Average Stone Size' }, 
          definition: { pt: 'Tamanho médio das pedras recuperadas', en: 'Average size of recovered stones' },
          actual: 0.42, budget: 0.45, yoy: 0.38, unit: 'ct/pedra',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Tamanho médio das pedras recuperadas - impacta directamente o preço médio de venda', en: 'Average size of recovered stones - directly impacts average selling price' }
        },
      ]
    },
    // 3. STOCKS DE MINÉRIO
    // Pergunta: O stock suporta continuidade e qualidade?
    // KPIs: Stockpile (Principal), Alimentação de Stockpile, Alimentação de Novo Stock
    // Removido: Teor Médio Stockpile (menos robusto documentalmente)
    stocks: {
      pillar: { pt: 'Stocks de Minério', en: 'Ore Stocks' },
      question: { pt: 'O stock suporta continuidade e qualidade?', en: 'Does stock support continuity & quality?' },
      icon: Layers,
      color: colors.pillars.stocks,
      kpis: [
        { 
          id: 'rom_stock', 
          label: { pt: 'Stockpile', en: 'Stockpile' }, 
          definition: { pt: 'Volume de minério em stockpile', en: 'Ore volume in stockpile' },
          actual: 112000, budget: 120000, yoy: 98000, unit: 'm³',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Volume de minério disponível em stockpile para alimentação da planta', en: 'Ore volume available in stockpile for plant feed' },
          isPrimary: true
        },
        { 
          id: 'feed_from_stockpile_pct', 
          label: { pt: 'Alimentação de Stockpile', en: 'Feed from Stockpile' }, 
          definition: { pt: 'Percentagem da alimentação da planta proveniente de stockpile', en: 'Percentage of plant feed from stockpile' },
          actual: 38.5, budget: 30.0, yoy: 32.0, unit: '%',
          inverse: true,
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Percentagem da alimentação da planta que provém do stockpile vs directamente da mina', en: 'Percentage of plant feed from stockpile vs direct from mine' }
        },
        { 
          id: 'new_stock_feed_pct', 
          label: { pt: 'Alimentação de Novo Stock', en: 'Feed from New Stock' }, 
          definition: { pt: 'Percentagem de stock novo (recente) na alimentação total', en: 'Percentage of new (recent) stock in total feed' },
          actual: 62.5, budget: 70.0, yoy: 58.0, unit: '%',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Peso do stock recentemente depositado na alimentação total da planta', en: 'Weight of recently deposited stock in total plant feed' }
        },
      ]
    },
    // 4. EFICIÊNCIA OPERACIONAL
    // Pergunta: Os activos estão a entregar produtividade?
    // KPIs: Disponibilidade Planta (Principal), Utilização Efectiva, Horas Paragem
    // Removido: Conversão para DMS (fica na página de detalhe)
    efficiency: {
      pillar: { pt: 'Eficiência Operacional', en: 'Operational Efficiency' },
      question: { pt: 'Os activos entregam produtividade?', en: 'Are assets delivering productivity?' },
      icon: Gauge,
      color: colors.pillars.efficiency,
      kpis: [
        { 
          id: 'plant_availability', 
          label: { pt: 'Disponibilidade Planta', en: 'Plant Availability' }, 
          definition: { pt: 'Tempo útil de operação da planta', en: 'Plant uptime availability' },
          actual: 93.8, budget: 94.5, yoy: 91.2, unit: '%',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Percentagem de tempo que a planta esteve operacional', en: 'Percentage of time plant was operational' },
          isPrimary: true
        },
        { 
          id: 'plant_utilization', 
          label: { pt: 'Utilização Efectiva', en: 'Effective Utilization' }, 
          definition: { pt: 'Utilização efectiva da planta principal', en: 'Effective utilization of main plant' },
          actual: 85.2, budget: 88.0, yoy: 82.5, unit: '%',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Percentagem de utilização efectiva da planta principal vs capacidade disponível', en: 'Effective utilization percentage of main plant vs available capacity' }
        },
        { 
          id: 'plant_downtime', 
          label: { pt: 'Horas Paragem', en: 'Downtime Hours' }, 
          definition: { pt: 'Total de horas de paragem da Lavaria Central', en: 'Total downtime hours of Central Plant' },
          actual: 485, budget: 400, yoy: 620, unit: 'hrs',
          inverse: true,
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Total Horas Mecânicas + Total Horas Produção da Lavaria Central - menor é melhor', en: 'Total Mechanical Hours + Total Production Hours of Central Plant - lower is better' }
        },
      ]
    },
    // 5. RISCO E ENTREGA DO PLANO
    // Pergunta: O plano operacional foi cumprido?
    // KPIs: Gap Quilates vs Orç. (Principal), Volume Total DMS, Volume Não Produtivo
    // Removido: Quilates Recuperados (já tratado noutras secções)
    risk: {
      pillar: { pt: 'Risco e Entrega', en: 'Risk & Delivery' },
      question: { pt: 'O plano operacional foi cumprido?', en: 'Was operational plan delivered?' },
      icon: AlertTriangle,
      color: colors.pillars.risk,
      kpis: [
        { 
          id: 'carats_gap_ytd', 
          label: { pt: 'Gap Quilates vs Orç.', en: 'Carats Gap vs Budget' }, 
          definition: { pt: 'Desvio absoluto acumulado de quilates recuperados face ao orçamento', en: 'Absolute cumulative recovered carat deviation vs budget' },
          actual: -4.7, budget: 0, yoy: -8.2, unit: 'Kcts',
          inverse: true,
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Desvio absoluto em quilates recuperados face ao orçamento - valor negativo indica shortfall de produção', en: 'Absolute recovered carat deviation vs budget - negative indicates production shortfall' },
          isPrimary: true
        },
        { 
          id: 'dms_volume', 
          label: { pt: 'Volume Total DMS', en: 'Total DMS Volume' }, 
          definition: { pt: 'Volume total processado no circuito DMS', en: 'Total volume processed in DMS circuit' },
          actual: 892, budget: 950, yoy: 845, unit: 'm³',
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Volume total processado no circuito de Dense Media Separation', en: 'Total volume processed in Dense Media Separation circuit' }
        },
        { 
          id: 'non_productive_volume', 
          label: { pt: 'Volume Não Produtivo', en: 'Non-Productive Volume' }, 
          definition: { pt: 'Volume de material movimentado sem contribuição para produção', en: 'Material volume moved without production contribution' },
          actual: 245000, budget: 180000, yoy: 310000, unit: 'm³',
          inverse: true,
          source: 'Relatório Mensal Operacional / POA 2025',
          tooltip: { pt: 'Volume de material movimentado que não contribuiu para produção - menor é melhor', en: 'Volume of material moved that did not contribute to production - lower is better' }
        },
      ]
    },
  },
  2026: {
    // 1. MINA E ACESSO AO MINÉRIO (1T 2026)
    // KPIs: Minério Extraído (Principal), Estéril Removido, Strip Ratio
    mine: {
      pillar: { pt: 'Mina e Acesso', en: 'Mine & Access' },
      question: { pt: 'Há minério suficiente e acessível?', en: 'Is there enough accessible ore?' },
      icon: Pickaxe,
      color: colors.pillars.mine,
      kpis: [
        { 
          id: 'ore_extracted', 
          label: { pt: 'Minério Extraído', en: 'Ore Extracted' }, 
          actual: 1080000, budget: 1150000, yoy: 1020000, unit: 'm³',
          inverse: false,
          tooltip: { pt: 'Volume de minério extraído da mina', en: 'Ore volume extracted from mine' },
          isPrimary: true
        },
        { 
          id: 'waste_removed', 
          label: { pt: 'Estéril Removido', en: 'Waste Removed' }, 
          actual: 2100000, budget: 2420000, yoy: 2090000, unit: 'm³',
          inverse: true,
          tooltip: { pt: 'Volume de estéril removido', en: 'Waste volume removed' }
        },
        { 
          id: 'strip_ratio', 
          label: { pt: 'Strip Ratio', en: 'Strip Ratio' }, 
          actual: 1.94, budget: 2.10, yoy: 2.05, unit: ':1',
          inverse: true,
          tooltip: { pt: 'Rácio estéril/minério', en: 'Waste/ore ratio' }
        },
      ]
    },
    // 2. PLANTA E RECUPERAÇÃO (1T 2026)
    // KPIs: Minério Tratado (Principal), Teor de Alimentação, Tamanho Médio da Pedra
    plant: {
      pillar: { pt: 'Planta e Recuperação', en: 'Plant & Recovery' },
      question: { pt: 'A planta converte minério em quilates?', en: 'Is plant converting ore to carats?' },
      icon: Factory,
      color: colors.pillars.plant,
      kpis: [
        { 
          id: 'ore_processed', 
          label: { pt: 'Minério Tratado', en: 'Ore Processed' }, 
          actual: 1080000, budget: 1150000, yoy: 1010000, unit: 'm³',
          tooltip: { pt: 'Volume de minério processado', en: 'Ore volume processed' },
          isPrimary: true
        },
        { 
          id: 'feed_grade', 
          label: { pt: 'Teor de Alimentação', en: 'Feed Grade' }, 
          actual: 15.74, budget: 16.10, yoy: 15.82, unit: 'cpht',
          tooltip: { pt: 'Teor médio de alimentação', en: 'Average feed grade' }
        },
        { 
          id: 'avg_stone_size', 
          label: { pt: 'Tamanho Médio da Pedra', en: 'Average Stone Size' }, 
          actual: 0.39, budget: 0.44, yoy: 0.41, unit: 'ct/pedra',
          tooltip: { pt: 'Tamanho médio das pedras recuperadas', en: 'Average size of recovered stones' }
        },
      ]
    },
    // 3. STOCKS DE MINÉRIO (1T 2026)
    // KPIs: Stockpile (Principal), Alimentação de Stockpile, Alimentação de Novo Stock
    stocks: {
      pillar: { pt: 'Stocks de Minério', en: 'Ore Stocks' },
      question: { pt: 'O stock suporta continuidade e qualidade?', en: 'Does stock support continuity & quality?' },
      icon: Layers,
      color: colors.pillars.stocks,
      kpis: [
        { 
          id: 'rom_stock', 
          label: { pt: 'Stockpile', en: 'Stockpile' }, 
          actual: 125000, budget: 110000, yoy: 108000, unit: 'm³',
          tooltip: { pt: 'Volume de minério disponível em stockpile', en: 'Available ore volume in stockpile' },
          isPrimary: true
        },
        { 
          id: 'feed_from_stockpile_pct', 
          label: { pt: 'Alimentação de Stockpile', en: 'Feed from Stockpile' }, 
          actual: 35.2, budget: 28.0, yoy: 30.5, unit: '%',
          inverse: true,
          tooltip: { pt: 'Percentagem da alimentação da planta proveniente de stockpile', en: 'Percentage of plant feed from stockpile' }
        },
        { 
          id: 'new_stock_feed_pct', 
          label: { pt: 'Alimentação de Novo Stock', en: 'Feed from New Stock' }, 
          actual: 65.8, budget: 72.0, yoy: 60.5, unit: '%',
          tooltip: { pt: 'Peso do stock recente na alimentação total', en: 'Weight of recent stock in total feed' }
        },
      ]
    },
    // 4. EFICIÊNCIA OPERACIONAL (1T 2026)
    // KPIs: Disponibilidade Planta (Principal), Utilização Efectiva, Horas Paragem
    efficiency: {
      pillar: { pt: 'Eficiência Operacional', en: 'Operational Efficiency' },
      question: { pt: 'Os activos entregam produtividade?', en: 'Are assets delivering productivity?' },
      icon: Gauge,
      color: colors.pillars.efficiency,
      kpis: [
        { 
          id: 'plant_availability', 
          label: { pt: 'Disponibilidade Planta', en: 'Plant Availability' }, 
          actual: 92.3, budget: 95.0, yoy: 93.2, unit: '%',
          tooltip: { pt: 'Disponibilidade da planta', en: 'Plant availability' },
          isPrimary: true
        },
        { 
          id: 'plant_utilization', 
          label: { pt: 'Utilização Efectiva', en: 'Effective Utilization' }, 
          actual: 82.8, budget: 87.0, yoy: 84.2, unit: '%',
          tooltip: { pt: 'Utilização efectiva da planta principal', en: 'Effective main plant utilization' }
        },
        { 
          id: 'plant_downtime', 
          label: { pt: 'Horas Paragem', en: 'Downtime Hours' }, 
          actual: 142, budget: 100, yoy: 125, unit: 'hrs',
          inverse: true,
          tooltip: { pt: 'Total Horas Mecânicas + Horas Produção da Lavaria Central', en: 'Total Mechanical + Production Hours of Central Plant' }
        },
      ]
    },
    // 5. RISCO E ENTREGA DO PLANO (1T 2026)
    // KPIs: Gap Quilates vs Orç. (Principal), Volume Total DMS, Volume Não Produtivo
    risk: {
      pillar: { pt: 'Risco e Entrega', en: 'Risk & Delivery' },
      question: { pt: 'O plano operacional foi cumprido?', en: 'Was operational plan delivered?' },
      icon: AlertTriangle,
      color: colors.pillars.risk,
      kpis: [
        { 
          id: 'carats_gap_ytd', 
          label: { pt: 'Gap Quilates vs Orç.', en: 'Carats Gap vs Budget' }, 
          actual: -2.4, budget: 0, yoy: -1.8, unit: 'Kcts',
          inverse: true,
          tooltip: { pt: 'Desvio absoluto em quilates recuperados face ao orçamento', en: 'Absolute recovered carat deviation vs budget' },
          isPrimary: true
        },
        { 
          id: 'dms_volume', 
          label: { pt: 'Volume Total DMS', en: 'Total DMS Volume' }, 
          actual: 215, budget: 240, yoy: 208, unit: 'm³',
          tooltip: { pt: 'Volume total processado no circuito DMS', en: 'Total volume processed in DMS circuit' }
        },
        { 
          id: 'non_productive_volume', 
          label: { pt: 'Volume Não Produtivo', en: 'Non-Productive Volume' }, 
          actual: 58000, budget: 45000, yoy: 72000, unit: 'm³',
          inverse: true,
          tooltip: { pt: 'Volume de material movimentado sem contribuição para produção', en: 'Material volume moved without production contribution' }
        },
      ]
    },
  }
};

// ============ HELPER FUNCTIONS ============
const formatValue = (value, unit) => {
  if (value === null || value === undefined) return '-';
  
  // Percentages and ratios - no K/M formatting
  if (unit === '%' || unit === ':1') return value.toFixed(1);
  if (unit === 'cpht') return value.toFixed(2);
  if (unit === 'ct/pedra') return value.toFixed(2);
  
  // Format large numbers with K (thousands) and M (millions)
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + 'K';
  }
  
  // Small numbers
  if (unit === 'Kcts') return value.toFixed(1);
  return value.toFixed(value % 1 === 0 ? 0 : 1);
};

const calculateDeviation = (actual, budget) => {
  if (!budget || budget === 0) return 0;
  return ((actual - budget) / Math.abs(budget)) * 100;
};

const getStatusColor = (deviation, inverse = false) => {
  const adjustedDev = inverse ? -deviation : deviation;
  if (adjustedDev >= 0) return colors.status.positive;
  if (adjustedDev >= -5) return colors.status.warning;
  return colors.status.negative;
};

const getStatusBg = (deviation, inverse = false) => {
  const adjustedDev = inverse ? -deviation : deviation;
  if (adjustedDev >= 0) return colors.status.positiveBg;
  if (adjustedDev >= -5) return colors.status.warningBg;
  return colors.status.negativeBg;
};

// ============ PRIMARY KPI CARD - KPI ÂNCORA - Máximo destaque visual ============
const PrimaryKPICard = ({ kpi, language }) => {
  const rawDeviation = calculateDeviation(kpi.actual, kpi.budget);
  const rawYoY = calculateDeviation(kpi.actual, kpi.yoy);
  
  const isPositiveOutcome = kpi.inverse ? rawDeviation < 0 : rawDeviation > 0;
  const isYoYPositive = kpi.inverse ? rawYoY < 0 : rawYoY > 0;

  return (
    <TooltipProvider>
      <ShadcnTooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div 
            className="px-4 py-5 hover:bg-slate-50/50 transition-colors cursor-help"
            style={{ 
              borderBottom: `1px solid ${colors.border.soft}`,
              background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)'
            }}
            data-testid={`ops-kpi-primary-${kpi.id}`}
          >
            {/* KPI Âncora Label - Bold, prominent */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div 
                  className="w-1.5 h-6 rounded-full"
                  style={{ backgroundColor: colors.accent.gold }}
                />
                <p className="text-[13px] font-bold text-slate-800 tracking-wide">
                  {kpi.label[language]}
                </p>
              </div>
              {/* Trend Circle - Larger, with shadow */}
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center shadow-md"
                style={{ 
                  backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg,
                  border: `2px solid ${isPositiveOutcome ? colors.status.positiveBorder : colors.status.negativeBorder}`
                }}
              >
                {isPositiveOutcome ? (
                  <ArrowUpRight className="w-4 h-4" style={{ color: colors.status.positive }} />
                ) : (
                  <ArrowDownRight className="w-4 h-4" style={{ color: colors.status.negative }} />
                )}
              </div>
            </div>
            
            {/* Large Value Display - Bigger, bolder */}
            <div className="flex items-baseline gap-1 mb-3">
              <span 
                className="text-3xl font-extrabold tracking-tight"
                style={{ color: colors.text.primary }}
              >
                {formatValue(kpi.actual, kpi.unit)}
              </span>
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                {kpi.unit}
              </span>
            </div>
            
            {/* Variance Badges - Inline side by side, compact fit */}
            <div className="flex items-center gap-1.5">
              {/* vs Budget */}
              <div 
                className="flex items-center gap-1 px-1.5 py-0.5 rounded"
                style={{ 
                  backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg,
                }}
              >
                {isPositiveOutcome ? (
                  <TrendingUp className="w-3 h-3" style={{ color: colors.status.positive }} />
                ) : (
                  <TrendingDown className="w-3 h-3" style={{ color: colors.status.negative }} />
                )}
                <span 
                  className="text-[9px] font-bold"
                  style={{ color: isPositiveOutcome ? colors.status.positive : colors.status.negative }}
                >
                  {rawDeviation >= 0 ? '+' : ''}{rawDeviation.toFixed(1)}%
                </span>
                <span 
                  className="text-[8px] font-semibold"
                  style={{ color: isPositiveOutcome ? colors.status.positive : colors.status.negative }}
                >
                  Orç
                </span>
              </div>
              
              {/* vs YoY */}
              <div 
                className="flex items-center gap-1 px-1.5 py-0.5 rounded"
                style={{ 
                  backgroundColor: isYoYPositive ? colors.status.positiveBg : colors.status.negativeBg,
                }}
              >
                {isYoYPositive ? (
                  <TrendingUp className="w-3 h-3" style={{ color: colors.status.positive }} />
                ) : (
                  <TrendingDown className="w-3 h-3" style={{ color: colors.status.negative }} />
                )}
                <span 
                  className="text-[9px] font-bold"
                  style={{ color: isYoYPositive ? colors.status.positive : colors.status.negative }}
                >
                  {rawYoY >= 0 ? '+' : ''}{rawYoY.toFixed(0)}%
                </span>
                <span 
                  className="text-[8px] font-semibold"
                  style={{ color: isYoYPositive ? colors.status.positive : colors.status.negative }}
                >
                  YoY
                </span>
              </div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs p-0 overflow-hidden rounded-lg"
          style={{ backgroundColor: colors.bg.dark, border: 'none' }}
        >
          <div className="px-3 py-2">
            <span className="text-sm font-semibold text-white">{kpi.label[language]}</span>
            <p className="text-xs text-slate-400 leading-relaxed mt-1">{kpi.tooltip[language]}</p>
            {kpi.inverse && (
              <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {language === 'pt' ? 'Menor é melhor' : 'Lower is better'}
              </p>
            )}
          </div>
        </TooltipContent>
      </ShadcnTooltip>
    </TooltipProvider>
  );
};

// ============ SUPPORT KPI CARD - KPIs de Suporte - Menor dominância ============
const SupportKPICard = ({ kpi, language }) => {
  const rawDeviation = calculateDeviation(kpi.actual, kpi.budget);
  const rawYoY = calculateDeviation(kpi.actual, kpi.yoy);
  
  const isPositiveOutcome = kpi.inverse ? rawDeviation < 0 : rawDeviation > 0;
  const isYoYPositive = kpi.inverse ? rawYoY < 0 : rawYoY > 0;

  return (
    <TooltipProvider>
      <ShadcnTooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div 
            className="px-3 py-2.5 border-b last:border-b-0 hover:bg-white/50 transition-colors cursor-help"
            style={{ borderColor: colors.border.soft }}
            data-testid={`ops-kpi-support-${kpi.id}`}
          >
            {/* Compact Row: Label + Mini Trend */}
            <div className="flex items-center justify-between mb-1">
              <p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">
                {kpi.label[language]}
              </p>
              {/* Micro Trend Dot */}
              <div 
                className="w-4 h-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg }}
              >
                {isPositiveOutcome ? (
                  <ArrowUpRight className="w-2.5 h-2.5" style={{ color: colors.status.positive }} />
                ) : (
                  <ArrowDownRight className="w-2.5 h-2.5" style={{ color: colors.status.negative }} />
                )}
              </div>
            </div>
            
            {/* Value - Slightly larger */}
            <div className="flex items-baseline gap-1 mb-1.5">
              <span className="text-lg font-bold text-slate-600 tracking-tight">
                {formatValue(kpi.actual, kpi.unit)}
              </span>
              <span className="text-[8px] text-slate-400 font-medium">
                {kpi.unit}
              </span>
            </div>
            
            {/* Variance Badges - Slightly larger for better readability */}
            <div className="flex gap-1.5">
              <span 
                className="text-[8px] font-bold px-1.5 py-0.5 rounded"
                style={{ 
                  color: isPositiveOutcome ? colors.status.positive : colors.status.negative,
                  backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg 
                }}
              >
                {rawDeviation >= 0 ? '+' : ''}{rawDeviation.toFixed(1)}% Orç
              </span>
              <span 
                className="text-[8px] font-bold px-1.5 py-0.5 rounded"
                style={{ 
                  color: isYoYPositive ? colors.status.positive : colors.status.negative,
                  backgroundColor: isYoYPositive ? colors.status.positiveBg : colors.status.negativeBg 
                }}
              >
                {rawYoY >= 0 ? '+' : ''}{rawYoY.toFixed(0)}% YoY
              </span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs p-0 overflow-hidden rounded-lg"
          style={{ backgroundColor: colors.bg.dark, border: 'none' }}
        >
          <div className="px-3 py-2">
            <span className="text-sm font-semibold text-white">{kpi.label[language]}</span>
            <p className="text-xs text-slate-400 leading-relaxed mt-1">{kpi.tooltip[language]}</p>
            {kpi.inverse && (
              <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {language === 'pt' ? 'Menor é melhor' : 'Lower is better'}
              </p>
            )}
          </div>
        </TooltipContent>
      </ShadcnTooltip>
    </TooltipProvider>
  );
};

// ============ PILLAR COMPONENT - Updated for 3 KPIs with Strong Hierarchy ============
const PillarColumn = ({ pillarData, language }) => {
  const Icon = pillarData.icon;
  const primaryKPI = pillarData.kpis.find(kpi => kpi.isPrimary);
  const supportKPIs = pillarData.kpis.filter(kpi => !kpi.isPrimary);
  
  return (
    <div className="flex flex-col h-full">
      {/* Pillar Header - Premium with stronger titles */}
      <div 
        className="px-3 py-3 rounded-t-lg"
        style={{ 
          backgroundColor: `${pillarData.color}08`,
          borderBottom: `3px solid ${pillarData.color}`,
        }}
      >
        <div className="flex items-center gap-2.5 mb-1.5">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
            style={{ backgroundColor: `${pillarData.color}15` }}
          >
            <Icon className="w-4.5 h-4.5" style={{ color: pillarData.color }} />
          </div>
          <span 
            className="text-[12px] font-extrabold tracking-wide uppercase"
            style={{ color: pillarData.color }}
          >
            {pillarData.pillar[language]}
          </span>
        </div>
        <p className="text-[9px] italic text-slate-500 leading-tight pl-[42px]">
          {pillarData.question[language]}
        </p>
      </div>
      
      {/* KPIs Container */}
      <div 
        className="flex-1 bg-white rounded-b-lg border border-t-0 shadow-sm overflow-hidden"
        style={{ borderColor: colors.border.default }}
      >
        {/* Primary KPI - KPI Âncora - Maximum prominence */}
        {primaryKPI && (
          <PrimaryKPICard kpi={primaryKPI} language={language} />
        )}
        
        {/* Separator - Visual break between Anchor and Support */}
        <div 
          className="h-px mx-3"
          style={{ backgroundColor: colors.border.soft }}
        />
        
        {/* Support KPIs - Reduced visual weight */}
        <div 
          className="py-1"
          style={{ backgroundColor: '#F8FAFC' }}
        >
          <p className="px-3 py-1 text-[7px] font-semibold uppercase tracking-wider text-slate-400">
            {language === 'pt' ? 'KPIs de Suporte' : 'Support KPIs'}
          </p>
          {supportKPIs.map((kpi) => (
            <SupportKPICard key={kpi.id} kpi={kpi} language={language} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN SCORECARD COMPONENT ============
const OperationsExecutiveScorecard = ({ year = 2025 }) => {
  const { language } = useLanguage();
  const data = scorecardData[year] || scorecardData[2025];
  const pillars = ['mine', 'plant', 'stocks', 'efficiency', 'risk'];
  
  const periodLabel = year === 2026 ? '1T 2026' : '2025';
  const budgetLabel = year === 2026 ? 'Orç. 1T26' : 'Orç. 2025';
  const yoyLabel = year === 2026 ? '1T25' : '2024';

  return (
    <div className="space-y-4" data-testid="operations-executive-scorecard">
      {/* Header - Premium Executive Style */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl font-semibold text-primary flex items-center gap-2">
            <Activity className="w-5 h-5" style={{ color: colors.accent.gold }} />
            {language === 'pt' ? 'Scorecard Operacional Executivo' : 'Executive Operations Scorecard'}
          </h2>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {language === 'pt' 
              ? `5 pilares × 3 KPIs | ${periodLabel} vs ${budgetLabel} e vs ${yoyLabel}`
              : `5 pillars × 3 KPIs | ${periodLabel} vs ${budgetLabel} and vs ${yoyLabel}`
            }
          </p>
        </div>
        
        {/* Legend - Minimal */}
        <div className="flex items-center gap-4 text-[10px]">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.status.positive }} />
            <span className="text-slate-500 font-medium">{language === 'pt' ? 'Favorável' : 'Favorable'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.status.negative }} />
            <span className="text-slate-500 font-medium">{language === 'pt' ? 'Atenção' : 'Attention'}</span>
          </div>
        </div>
      </div>
      
      {/* 5 Pillars Grid - Balanced spacing */}
      <div className="grid grid-cols-5 gap-3">
        {pillars.map((pillarKey) => (
          <PillarColumn 
            key={pillarKey} 
            pillarData={data[pillarKey]} 
            language={language}
          />
        ))}
      </div>
    </div>
  );
};

export default OperationsExecutiveScorecard;
